import React from "react";

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

// `<input type="color">` rejects anything that isn't `#rrggbb` and logs a
// console warning. The text field is free-form (users can be mid-edit, blank,
// or pasting `#fff`). Derive a safe value for the native picker so it never
// receives an invalid string while letting the text field stay in sync.
const HEX6 = /^#[0-9a-fA-F]{6}$/;
function sanitize(value: string | undefined | null): string {
  if (!value) return "#000000";
  if (HEX6.test(value)) return value;
  // Expand short form `#abc` → `#aabbcc` for the picker only.
  const short = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/.exec(value);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  return "#000000";
}

export default function ColorPicker(props: Props) {
  const safeValue = sanitize(props.value);
  return (
    <div className="inline-block relative">
      <div
        className={`flex items-center border p-2 rounded-lg w-32 space-x-2 ${
          props.disabled ? "bg-slate-100 opacity-60 cursor-not-allowed" : ""
        }`}
      >
        <div className="relative h-5 w-5 overflow-hidden border border-slate-300">
          <input
            type="color"
            disabled={props.disabled}
            className="absolute top-[-10px] left-[-10px] h-10 w-10 cursor-pointer disabled:cursor-not-allowed"
            value={safeValue}
            onChange={(e) => props.onChange(e.currentTarget.value)}
          />
        </div>
        <input
          type="text"
          disabled={props.disabled}
          value={props.value ?? ""}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          className="w-full rounded px-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-slate-100 disabled:cursor-not-allowed"
          maxLength={7}
          placeholder="#FFFFFF"
        />
      </div>
    </div>
  );
}
